import { useCallback, useEffect, useMemo, useState } from "react";
import {
	Building2,
	ChevronDown,
	Info,
	Plus,
	Trash2,
	UserMinus,
	UserPlus,
	Users,
	X,
} from "lucide-react";
import { toast } from "sonner";

import { Badge } from "./ui/badge";
import { Button } from "./ui/button";
import { Card, CardContent } from "./ui/card";
import {
	Collapsible,
	CollapsibleContent,
	CollapsibleTrigger,
} from "./ui/collapsible";
import {
	Dialog,
	DialogContent,
	DialogDescription,
	DialogHeader,
	DialogTitle,
} from "./ui/dialog";
import { Input } from "./ui/input";
import { Label } from "./ui/label";
import {
	Select,
	SelectContent,
	SelectItem,
	SelectTrigger,
	SelectValue,
} from "./ui/select";
import { Textarea } from "./ui/textarea";
import { useAuth } from "../contexts/AuthContext";
import { useLanguage } from "../contexts/LanguageContext";
import { matchesCoachAthleteSearch } from "../lib/coachUtils";
import { fetchAPI, formatFullName } from "../lib/utils";

const GUIDE_STORAGE_KEY = "coach_teams_guide_dismissed";

/**
 * Gestione dei coach team lato coach.
 *
 * Un coach amministra i team che ha creato e vede quelli di cui fa parte. Il
 * pannello admin (`CoachManagement.js`) resta separato: li' si amministrano i
 * coach della piattaforma, qui i propri team.
 *
 * Spec: docs/coach-teams/PLAN.md Fase 1.
 */
export function CoachTeamsTab() {
	const { user } = useAuth();
	const { t } = useLanguage();

	const [teams, setTeams] = useState([]);
	const [candidates, setCandidates] = useState([]);
	const [athletes, setAthletes] = useState([]);
	const [loading, setLoading] = useState(true);
	const [guideOpen, setGuideOpen] = useState(() => {
		if (typeof window === "undefined") return true;
		try {
			return window.localStorage.getItem(GUIDE_STORAGE_KEY) !== "1";
		} catch {
			return true;
		}
	});

	const [isCreateOpen, setIsCreateOpen] = useState(false);
	const [form, setForm] = useState({ name: "", description: "" });
	const [saving, setSaving] = useState(false);
	const [editingTeamId, setEditingTeamId] = useState(null);
	const [editName, setEditName] = useState("");
	const [coachSelection, setCoachSelection] = useState({});
	const [athletesDialogTeam, setAthletesDialogTeam] = useState(null);
	const [athleteSearch, setAthleteSearch] = useState("");

	const loadData = useCallback(async () => {
		try {
			const [teamsData, candidatesData, athletesData] = await Promise.all([
				fetchAPI("/teams/mine").catch(() => []),
				fetchAPI("/teams/candidates").catch(() => []),
				fetchAPI("/users/athletes").catch(() => []),
			]);
			setTeams(teamsData || []);
			setCandidates(candidatesData || []);
			setAthletes(athletesData || []);
		} finally {
			setLoading(false);
		}
	}, []);

	useEffect(() => {
		loadData();
	}, [loadData]);

	const dismissGuide = () => {
		setGuideOpen(false);
		try {
			window.localStorage.setItem(GUIDE_STORAGE_KEY, "1");
		} catch {
			/* niente: la guida si richiude comunque per questa sessione */
		}
	};

	const handleCreate = async (event) => {
		event.preventDefault();
		if (saving) return;
		setSaving(true);
		try {
			await fetchAPI("/teams?personal=true", {
				method: "POST",
				body: JSON.stringify(form),
			});
			toast.success(t("coachTeamsCreated"));
			setIsCreateOpen(false);
			setForm({ name: "", description: "" });
			loadData();
		} catch (error) {
			toast.error(error?.message || t("saveError"));
		} finally {
			setSaving(false);
		}
	};

	const handleRename = async (teamId) => {
		const name = editName.trim();
		if (!name) return;
		try {
			await fetchAPI(`/teams/${teamId}`, {
				method: "PUT",
				body: JSON.stringify({ name }),
			});
			setEditingTeamId(null);
			loadData();
		} catch (error) {
			toast.error(error?.message || t("saveError"));
		}
	};

	const handleDelete = async (team) => {
		if (!window.confirm(t("coachTeamsDeleteConfirm"))) return;
		try {
			await fetchAPI(`/teams/${team.team_id}`, { method: "DELETE" });
			toast.success(t("coachTeamsDeleted"));
			loadData();
		} catch (error) {
			toast.error(error?.message || t("saveError"));
		}
	};

	const handleCoach = async (teamId, coachId, action) => {
		if (!coachId) return;
		try {
			await fetchAPI(`/teams/${teamId}/coaches`, {
				method: "PUT",
				body: JSON.stringify({ coach_id: coachId, action }),
			});
			setCoachSelection((prev) => ({ ...prev, [teamId]: "" }));
			loadData();
		} catch (error) {
			toast.error(error?.message || t("saveError"));
		}
	};

	const handleAthletes = async (teamId, athleteIds, action) => {
		if (!athleteIds.length) return;
		try {
			const result = await fetchAPI(`/teams/${teamId}/athletes`, {
				method: "PUT",
				body: JSON.stringify({ athlete_ids: athleteIds, action }),
			});
			// Il server ricontrolla ogni atleta contro il cono di chi chiede: se
			// qualcuno resta fuori va detto, non ignorato in silenzio.
			if (result?.skipped?.length) {
				toast.warning(t("coachTeamsAthletesSkipped"));
			}
			loadData();
		} catch (error) {
			toast.error(error?.message || t("saveError"));
		}
	};

	const athletesByTeam = useMemo(() => {
		const map = {};
		for (const athlete of athletes) {
			for (const teamId of athlete.coach_team_ids || []) {
				(map[teamId] = map[teamId] || []).push(athlete);
			}
		}
		return map;
	}, [athletes]);

	const dialogTeam = useMemo(
		() => teams.find((team) => team.team_id === athletesDialogTeam) || null,
		[teams, athletesDialogTeam],
	);

	const dialogMembers = dialogTeam
		? athletesByTeam[dialogTeam.team_id] || []
		: [];
	const dialogMemberIds = new Set(dialogMembers.map((a) => a.user_id));
	const dialogCandidates = athletes
		.filter((a) => !dialogMemberIds.has(a.user_id))
		.filter((a) => matchesCoachAthleteSearch(a, athleteSearch));

	if (loading) {
		return (
			<div className="flex items-center justify-center min-h-[60vh]">
				<div className="spinner" />
			</div>
		);
	}

	const guideRows = [
		["coachTeamsGuideWhat", "coachTeamsGuideWhatBody"],
		["coachTeamsGuideOwner", "coachTeamsGuideOwnerBody"],
		["coachTeamsGuideMember", "coachTeamsGuideMemberBody"],
		["coachTeamsGuideShare", "coachTeamsGuideShareBody"],
		["coachTeamsGuideAdd", "coachTeamsGuideAddBody"],
	];

	return (
		<div className="max-w-5xl mx-auto px-4 md:px-6 py-6 md:py-8 space-y-4">
			<div className="flex items-start justify-between gap-3 flex-wrap">
				<div className="min-w-0">
					<h1 className="font-heading text-2xl">{t("coachTeams")}</h1>
					<p className="text-sm text-muted-foreground mt-0.5">
						{t("coachTeamsSubtitle")}
					</p>
				</div>
				<div className="flex items-center gap-2">
					{!guideOpen && (
						<Button
							variant="ghost"
							size="sm"
							className="rounded-sm"
							onClick={() => setGuideOpen(true)}
						>
							<Info className="w-4 h-4 mr-1.5" />
							{t("coachTeamsGuideShow")}
						</Button>
					)}
					<Button
						className="bg-volt hover:bg-volt/90 rounded-sm"
						onClick={() => setIsCreateOpen(true)}
					>
						<Plus className="w-4 h-4 mr-1.5" />
						{t("coachTeamsNew")}
					</Button>
				</div>
			</div>

			{/* Guida al flusso. Si chiude e resta chiusa: serve la prima volta, non
			    tutte le volte. */}
			{guideOpen && (
				<Card className="rounded-sm border-volt/40 bg-volt/[0.04]">
					<CardContent className="p-4 space-y-3">
						<div className="flex items-start justify-between gap-3">
							<h2 className="font-heading text-base flex items-center gap-2">
								<Info className="w-4 h-4 text-volt" />
								{t("coachTeamsGuideTitle")}
							</h2>
							<button
								type="button"
								onClick={dismissGuide}
								className="text-muted-foreground hover:text-foreground shrink-0"
								aria-label={t("close")}
							>
								<X className="w-4 h-4" />
							</button>
						</div>
						<dl className="space-y-2.5">
							{guideRows.map(([titleKey, bodyKey]) => (
								<div key={titleKey}>
									<dt className="text-xs font-medium uppercase tracking-wider text-volt">
										{t(titleKey)}
									</dt>
									<dd className="text-sm text-muted-foreground mt-0.5">
										{t(bodyKey)}
									</dd>
								</div>
							))}
						</dl>
					</CardContent>
				</Card>
			)}

			{teams.length === 0 ? (
				<Card className="rounded-sm">
					<CardContent className="py-12 text-center space-y-2">
						<Building2 className="w-8 h-8 mx-auto text-muted-foreground/60" />
						<p className="text-sm text-muted-foreground">
							{t("coachTeamsEmpty")}
						</p>
					</CardContent>
				</Card>
			) : (
				<div className="space-y-3">
					{teams.map((team) => {
						const teamAthletes = athletesByTeam[team.team_id] || [];
						const available = candidates.filter(
							(c) =>
								c.user_id !== user?.user_id &&
								!(team.coach_ids || []).includes(c.user_id),
						);
						const isEditing = editingTeamId === team.team_id;

						return (
							<Card key={team.team_id} className="bg-card rounded-sm">
								<CardContent className="p-4 space-y-3">
									<div className="flex items-start justify-between gap-3 flex-wrap">
										<div className="min-w-0 flex-1">
											{isEditing ? (
												<div className="flex items-center gap-2">
													<Input
														value={editName}
														onChange={(e) => setEditName(e.target.value)}
														className="h-8 rounded-sm bg-secondary border-transparent max-w-xs"
													/>
													<Button
														size="sm"
														className="rounded-sm h-8 bg-volt hover:bg-volt/90"
														onClick={() => handleRename(team.team_id)}
													>
														{t("save")}
													</Button>
													<Button
														size="sm"
														variant="ghost"
														className="rounded-sm h-8"
														onClick={() => setEditingTeamId(null)}
													>
														{t("cancel")}
													</Button>
												</div>
											) : (
												<h3 className="font-heading text-lg flex items-center gap-2 flex-wrap">
													<Building2 className="w-4 h-4 text-acid shrink-0" />
													{team.name}
													{team.is_platform_team ? (
														<Badge variant="secondary" className="rounded-sm">
															{t("coachTeamsPlatformBadge")}
														</Badge>
													) : team.is_owner ? (
														<Badge className="rounded-sm bg-volt/15 text-volt border border-volt/40">
															{t("coachTeamsOwnerBadge")}
														</Badge>
													) : (
														<Badge variant="secondary" className="rounded-sm">
															{t("coachTeamsMemberBadge")}
														</Badge>
													)}
												</h3>
											)}
											{team.description && !isEditing && (
												<p className="text-sm text-muted-foreground mt-0.5">
													{team.description}
												</p>
											)}
										</div>

										{team.is_owner && !team.is_platform_team && !isEditing && (
											<div className="flex items-center gap-1 shrink-0">
												<Button
													variant="ghost"
													size="sm"
													className="rounded-sm h-8"
													onClick={() => {
														setEditingTeamId(team.team_id);
														setEditName(team.name || "");
													}}
												>
													{t("rename")}
												</Button>
												<Button
													variant="ghost"
													size="icon"
													className="rounded-sm h-8 w-8 text-muted-foreground hover:text-blaze"
													onClick={() => handleDelete(team)}
													aria-label={t("delete")}
												>
													<Trash2 className="w-4 h-4" />
												</Button>
											</div>
										)}
									</div>

									{/* Cosa arriva attraverso questo team: e' la risposta a
									    «perche' vedo questa roba». */}
									<div className="flex flex-wrap gap-x-4 gap-y-1 text-xs text-muted-foreground">
										<span>
											{t("coachTeamsCoachCount")}: {team.coach_count ?? 0}
										</span>
										<span>
											{t("coachTeamsAthleteCount")}: {team.athlete_count ?? 0}
										</span>
										<span>
											{t("coachTeamsSharedFolders")}: {team.shared_folders ?? 0}
										</span>
										<span>
											{t("coachTeamsSharedPrograms")}:{" "}
											{team.shared_programs ?? 0}
										</span>
										<span>
											{t("coachTeamsSharedGroups")}: {team.shared_groups ?? 0}
										</span>
									</div>

									<Collapsible>
										<CollapsibleTrigger asChild>
											<button
												type="button"
												className="group w-full flex items-center justify-between gap-2 px-3 py-2 rounded-sm bg-secondary/60 border border-border/60 hover:bg-secondary text-left transition-colors"
											>
												<span className="flex items-center gap-2 min-w-0">
													<Users className="w-4 h-4 text-muted-foreground shrink-0" />
													<span className="text-xs font-medium uppercase tracking-wider truncate">
														{t("coachTeamsMembers")}
													</span>
												</span>
												<ChevronDown className="w-4 h-4 text-muted-foreground shrink-0 transition-transform group-data-[state=open]:rotate-180" />
											</button>
										</CollapsibleTrigger>
										<CollapsibleContent>
											<div className="mt-3 space-y-4">
												<div className="space-y-2">
													<Label className="label-tactical">
														{t("coachTeamsCoaches")}
													</Label>
													<div className="flex flex-wrap gap-2">
														{(team.coach_ids || []).map((coachId) => {
															const coach = candidates.find(
																(c) => c.user_id === coachId,
															);
															const isTeamOwner = coachId === team.owner_id;
															return (
																<span
																	key={coachId}
																	className="flex items-center gap-1 px-2 py-1 bg-secondary rounded-sm text-xs"
																>
																	{coach
																		? formatFullName(coach)
																		: coachId === user?.user_id
																			? formatFullName(user)
																			: coachId}
																	{isTeamOwner && (
																		<span className="text-volt/80">
																			· {t("coachTeamsOwnerShort")}
																		</span>
																	)}
																	{team.is_owner && !isTeamOwner && (
																		<button
																			type="button"
																			onClick={() =>
																				handleCoach(
																					team.team_id,
																					coachId,
																					"remove",
																				)
																			}
																			className="text-muted-foreground hover:text-blaze"
																			aria-label={t("remove")}
																		>
																			<UserMinus className="w-3 h-3" />
																		</button>
																	)}
																</span>
															);
														})}
													</div>

													{team.is_owner && !team.is_platform_team && (
														<div className="flex gap-2 pt-1">
															<Select
																value={
																	coachSelection[team.team_id] || undefined
																}
																onValueChange={(value) =>
																	setCoachSelection((prev) => ({
																		...prev,
																		[team.team_id]: value,
																	}))
																}
															>
																<SelectTrigger className="bg-secondary border-transparent rounded-sm h-8">
																	<SelectValue
																		placeholder={
																			available.length
																				? t("coachTeamsAddCoach")
																				: t("coachTeamsNoCandidates")
																		}
																	/>
																</SelectTrigger>
																<SelectContent className="bg-card border-border">
																	{available.map((coach) => (
																		<SelectItem
																			key={coach.user_id}
																			value={coach.user_id}
																		>
																			{formatFullName(coach)}
																			{coach.email ? ` · ${coach.email}` : ""}
																		</SelectItem>
																	))}
																</SelectContent>
															</Select>
															<Button
																variant="outline"
																size="sm"
																className="rounded-sm h-8 shrink-0"
																disabled={!coachSelection[team.team_id]}
																onClick={() =>
																	handleCoach(
																		team.team_id,
																		coachSelection[team.team_id],
																		"add",
																	)
																}
															>
																<UserPlus className="w-3.5 h-3.5 mr-1.5" />
																{t("add")}
															</Button>
														</div>
													)}
												</div>

												<div className="space-y-2">
													<Label className="label-tactical">
														{t("coachTeamsAthletes")}
													</Label>
													{teamAthletes.length === 0 ? (
														<p className="text-xs text-muted-foreground">
															{t("coachTeamsNoAthletes")}
														</p>
													) : (
														<div className="flex flex-wrap gap-2">
															{teamAthletes.map((athlete) => (
																<span
																	key={athlete.user_id}
																	className="flex items-center gap-1 px-2 py-1 bg-secondary rounded-sm text-xs"
																>
																	{formatFullName(athlete)}
																	{team.is_owner && (
																		<button
																			type="button"
																			onClick={() =>
																				handleAthletes(
																					team.team_id,
																					[athlete.user_id],
																					"remove",
																				)
																			}
																			className="text-muted-foreground hover:text-blaze"
																			aria-label={t("remove")}
																		>
																			<X className="w-3 h-3" />
																		</button>
																	)}
																</span>
															))}
														</div>
													)}
													{team.is_owner && !team.is_platform_team && (
														<Button
															variant="outline"
															size="sm"
															className="rounded-sm h-8"
															onClick={() => {
																setAthletesDialogTeam(team.team_id);
																setAthleteSearch("");
															}}
														>
															<UserPlus className="w-3.5 h-3.5 mr-1.5" />
															{t("coachTeamsAddAthletes")}
														</Button>
													)}
												</div>
											</div>
										</CollapsibleContent>
									</Collapsible>
								</CardContent>
							</Card>
						);
					})}
				</div>
			)}

			<Dialog open={isCreateOpen} onOpenChange={setIsCreateOpen}>
				<DialogContent className="bg-card border-border rounded-sm">
					<DialogHeader>
						<DialogTitle className="font-heading">
							{t("coachTeamsNew")}
						</DialogTitle>
						<DialogDescription>{t("coachTeamsNewHint")}</DialogDescription>
					</DialogHeader>
					<form onSubmit={handleCreate} className="space-y-4">
						<div className="space-y-2">
							<Label className="label-tactical">{t("name")}</Label>
							<Input
								value={form.name}
								onChange={(e) => setForm({ ...form, name: e.target.value })}
								className="bg-secondary border-transparent rounded-sm"
								placeholder={t("coachTeamsNamePlaceholder")}
								required
							/>
						</div>
						<div className="space-y-2">
							<Label className="label-tactical">{t("description")}</Label>
							<Textarea
								value={form.description}
								onChange={(e) =>
									setForm({ ...form, description: e.target.value })
								}
								className="bg-secondary border-transparent rounded-sm min-h-[70px]"
							/>
						</div>
						<Button
							type="submit"
							disabled={saving}
							className="w-full bg-volt hover:bg-volt/90 rounded-sm"
						>
							{t("coachTeamsNew")}
						</Button>
					</form>
				</DialogContent>
			</Dialog>

			<Dialog
				open={Boolean(athletesDialogTeam)}
				onOpenChange={(open) => !open && setAthletesDialogTeam(null)}
			>
				<DialogContent className="bg-card border-border rounded-sm max-h-[85vh] overflow-y-auto">
					<DialogHeader>
						<DialogTitle className="font-heading">
							{t("coachTeamsAddAthletes")}
						</DialogTitle>
						<DialogDescription>
							{t("coachTeamsAddAthletesHint")}
						</DialogDescription>
					</DialogHeader>
					<Input
						value={athleteSearch}
						onChange={(e) => setAthleteSearch(e.target.value)}
						className="bg-secondary border-transparent rounded-sm"
						placeholder={t("searchAthlete")}
					/>
					<div className="space-y-1 mt-2">
						{dialogCandidates.length === 0 ? (
							<p className="text-xs text-muted-foreground py-4 text-center">
								{t("coachTeamsNoAthletesToAdd")}
							</p>
						) : (
							dialogCandidates.map((athlete) => (
								<button
									key={athlete.user_id}
									type="button"
									onClick={() =>
										handleAthletes(dialogTeam.team_id, [athlete.user_id], "add")
									}
									className="w-full flex items-center justify-between gap-2 px-3 py-2 rounded-sm hover:bg-secondary text-left"
								>
									<span className="min-w-0">
										<span className="block truncate text-sm">
											{formatFullName(athlete)}
										</span>
										{athlete.email && (
											<span className="block truncate text-xs text-muted-foreground">
												{athlete.email}
											</span>
										)}
									</span>
									<UserPlus className="w-4 h-4 text-volt shrink-0" />
								</button>
							))
						)}
					</div>
				</DialogContent>
			</Dialog>
		</div>
	);
}
